import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { MessageSquare, Zap, Clock, Shield, ArrowRight, Bot, Sparkles } from "lucide-react";

const ChatbotProductSection = () => {
  const features = [
    {
      icon: Clock,
      title: "24/7 verfügbar",
      desc: "Ihr Chatbot beantwortet Kundenanfragen rund um die Uhr – auch nachts und am Wochenende."
    },
    {
      icon: Zap,
      title: "Sofortige Antworten",
      desc: "Keine Wartezeiten mehr. Kunden erhalten sofort qualifizierte Antworten auf ihre Fragen."
    },
    {
      icon: Shield,
      title: "Einfache Integration",
      desc: "In wenigen Minuten auf Ihrer Website eingebunden – keine technischen Kenntnisse nötig."
    }
  ];

  return (
    <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-200/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200/30 rounded-full blur-3xl" />
      </div>
      
      <div className="mx-auto w-full max-w-7xl px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-purple-100 to-indigo-100 px-5 py-2.5 text-sm font-semibold text-purple-700 mb-6 border border-purple-200">
            <Sparkles className="h-4 w-4" />
            NEU: KI-Chatbot Service
          </div>
          
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6">
            Automatisieren Sie Ihren
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600">
              Kundenservice mit KI
            </span>
          </h2>
          
          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Unser KI-Chatbot beantwortet Kundenanfragen auf Ihrer Website – intelligent, 
            freundlich und immer verfügbar. Perfekt für kleine Unternehmen und Selbstständige.
          </p>
        </div>
        
        {/* Features Grid */}
        <div className="grid gap-8 md:grid-cols-3 mb-12">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div
                key={i}
                className="group relative bg-white rounded-2xl p-8 shadow-lg border border-slate-200/60 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
              >
                <div className="mb-6">
                  <div className="inline-flex rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 p-4 shadow-lg">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.desc}</p>
              </div>
            );
          })}
        </div>
        
        {/* CTA Box */}
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-slate-200/60">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            {/* Left: Bot Illustration */}
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="w-32 h-32 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-3xl flex items-center justify-center shadow-2xl">
                  <Bot className="h-16 w-16 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                  <MessageSquare className="h-4 w-4 text-white" />
                </div>
              </div>
            </div>
            
            {/* Right: Content */}
            <div className="flex-1 text-center lg:text-left">
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                Starten Sie kostenlos – ohne Risiko
              </h3>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Testen Sie unseren KI-Chatbot mit 10 kostenlosen Nachrichten pro Tag. 
                Keine Kreditkarte erforderlich. Upgrade jederzeit möglich.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/chatbot-produkt">
                  <Button className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-8 py-6 text-lg font-bold rounded-xl shadow-lg transition-all">
                    Jetzt kostenlos testen
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/chatbot-produkt">
                  <Button variant="outline" className="border-2 border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-6 text-lg font-bold rounded-xl transition-all">
                    Mehr erfahren
                  </Button>
                </Link>
              </div>
            </div>
          </div>
          
          {/* Pricing Preview */}
          <div className="mt-10 pt-8 border-t border-slate-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-3xl font-black text-slate-900">0€</div>
                <div className="text-sm text-slate-600 font-medium">Kostenlos starten</div>
              </div>
              <div>
                <div className="text-3xl font-black text-slate-900">10</div>
                <div className="text-sm text-slate-600 font-medium">Nachrichten/Tag gratis</div>
              </div>
              <div>
                <div className="text-3xl font-black text-slate-900">5 Min</div>
                <div className="text-sm text-slate-600 font-medium">Setup-Zeit</div>
              </div>
              <div>
                <div className="text-3xl font-black text-slate-900">24/7</div>
                <div className="text-sm text-slate-600 font-medium">Verfügbarkeit</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChatbotProductSection;
